import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput } from "@/components/ui/Field";
import {
  TimesheetFlowFooterButton,
} from "@/features/timesheetFlow/components/TimesheetFlowFooter";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption, LockedAutocompleteHandle } from "@/components/LockedAutocomplete";
import type { HeroArticleSuggestion } from "@/lib/material";
import {
  filterMaterialUnits,
  isAllowedMaterialUnit,
  normalizeMaterialUnit,
} from "@/lib/materialUnits";
import {
  isAngebotMaterialRecord,
  isHeroMaterialOption,
  manualMaterialSuggestion,
  manualMaterialToOption,
  materialRecordToOption,
  timesheetMaterialSource,
} from "@/features/timesheetFlow/lib/timesheetMaterialSource";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

interface MaterialFormState {
  text: string;
  menge: string;
  einheit: string;
  ean: string;
  hero_nr: string;
  artikel_id: number | null;
  hero_unit: string | null;
  source_type: string;
}

function materialToForm(material: TimesheetMaterialRecord | null | undefined): MaterialFormState {
  if (!material) {
    return {
      text: "",
      menge: "",
      einheit: "Stk",
      ean: "",
      hero_nr: "",
      artikel_id: null,
      hero_unit: null,
      source_type: "manual",
    };
  }
  return {
    text: material.text || "",
    menge: material.menge != null ? String(material.menge) : "",
    einheit: normalizeMaterialUnit(material.einheit) || "Stk",
    ean: material.ean || "",
    hero_nr: material.hero_nr || "",
    artikel_id: material.artikel_id ?? null,
    hero_unit: material.hero_unit ?? null,
    source_type: material.source_type || "manual",
  };
}

function formToMaterial(form: MaterialFormState, existing?: TimesheetMaterialRecord | null): TimesheetMaterialRecord {
  const mengeRaw = form.menge.trim();
  const menge = mengeRaw ? parseFloat(mengeRaw.replace(",", ".")) || 0 : 1;
  return {
    ...(existing ?? {}),
    text: form.text.trim(),
    menge,
    einheit: form.einheit.trim() || "Stk",
    ean: form.ean.trim() || null,
    hero_nr: form.hero_nr.trim() || null,
    artikel_id: form.artikel_id,
    hero_unit: form.hero_unit,
    source_type: form.source_type || "manual",
  };
}

function applyHeroArticle(form: MaterialFormState, article: HeroArticleSuggestion): MaterialFormState {
  const allowedUnit = normalizeMaterialUnit(article.einheit || article.hero_unit);
  if (article.artikel_id) {
    return {
      ...form,
      text: article.label,
      einheit: allowedUnit || form.einheit || "Stk",
      artikel_id: article.artikel_id,
      hero_nr: article.hero_nr || "",
      hero_unit: article.hero_unit,
      ean: article.ean || form.ean,
      source_type: "hero",
    };
  }
  return {
    ...form,
    text: article.label,
    artikel_id: null,
    hero_nr: "",
    hero_unit: null,
    source_type: "manual",
  };
}

export function TimesheetMaterialDialog({
  open,
  editMaterial,
  onClose,
  onSave,
  zIndexClass,
}: {
  open: boolean;
  editMaterial: TimesheetMaterialRecord | null;
  onClose: () => void;
  onSave: (material: TimesheetMaterialRecord, mode: "create" | "edit") => void;
  zIndexClass?: string;
}) {
  const layers = useTimesheetFlowLayers();
  const resolvedZIndexClass = zIndexClass ?? layers.overlay;
  const mode = editMaterial ? "edit" : "create";
  const angebotLocked = Boolean(editMaterial && isAngebotMaterialRecord(editMaterial));
  const [form, setForm] = useState<MaterialFormState>(() => materialToForm(editMaterial));
  const [baseline, setBaseline] = useState<MaterialFormState>(() => materialToForm(editMaterial));
  const [materialOption, setMaterialOption] = useState<AutocompleteOption<HeroArticleSuggestion> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const materialAcRef = useRef<LockedAutocompleteHandle>(null);

  useEffect(() => {
    if (!open) return;
    const nextForm = materialToForm(editMaterial);
    setForm(nextForm);
    setBaseline(nextForm);
    setMaterialOption(materialRecordToOption(nextForm));
    setError(null);
  }, [open, editMaterial]);

  const dirty = useMemo(() => hasFormChanges(form, baseline), [form, baseline]);

  const patch = (p: Partial<MaterialFormState>) => setForm((prev) => ({ ...prev, ...p }));

  const unitValue: AutocompleteOption<string> | null = form.einheit
    ? { key: form.einheit, label: form.einheit, value: form.einheit }
    : null;

  const handleMaterialChange = (opt: AutocompleteOption<HeroArticleSuggestion> | null) => {
    setMaterialOption(opt);
    if (!opt) {
      setForm((prev) => ({
        ...prev,
        text: "",
        artikel_id: null,
        hero_nr: "",
        hero_unit: null,
        ean: "",
        source_type: "manual",
      }));
      return;
    }
    setForm((prev) => applyHeroArticle(prev, opt.value));
  };

  const resolveMaterialLabel = () => {
    const pending = materialAcRef.current?.getPendingQuery()?.trim() ?? "";
    if (materialOption?.label?.trim()) return materialOption.label.trim();
    if (pending) return pending;
    return form.text.trim();
  };

  const handleSubmit = () => {
    if (angebotLocked && editMaterial) {
      const mengeRaw = form.menge.trim();
      const menge = mengeRaw ? parseFloat(mengeRaw.replace(",", ".")) || 0 : 0;
      if (!Number.isFinite(menge) || menge <= 0) {
        setError("Menge muss größer als 0 sein.");
        return;
      }
      onSave({ ...editMaterial, menge }, mode);
      onClose();
      return;
    }

    const label = resolveMaterialLabel();
    if (!label) {
      setError("Bitte ein Material auswählen oder eingeben.");
      return;
    }

    const nextForm =
      materialOption?.value?.artikel_id && materialOption.label.trim() === label
        ? form
        : applyHeroArticle({ ...form, text: label }, manualMaterialSuggestion(label));

    const einheit = normalizeMaterialUnit(nextForm.einheit) || "Stk";
    if (!isAllowedMaterialUnit(einheit)) {
      setError("Bitte eine Einheit aus der Liste auswählen.");
      return;
    }

    onSave(formToMaterial({ ...nextForm, text: label, einheit }, editMaterial), mode);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={
        angebotLocked
          ? "Menge anpassen"
          : mode === "edit"
            ? "Material bearbeiten"
            : "Material auswählen"
      }
      size="xl"
      zIndexClass={resolvedZIndexClass}
      fullScreenMobile
      bodyClassName="timesheet-flow-shell"
      footer={
        <MaterialDialogFooter onClose={onClose} onSubmit={handleSubmit} />
      }
    >
      <div className="space-y-3">
        {angebotLocked ? (
          <>
            <p className="text-sm text-label-secondary">
              Position aus dem Angebot – außer der Menge sind die Felder gesperrt.
            </p>
            <div className="rounded-xl border border-separator bg-fill-tertiary/40 px-3 py-2 text-sm">
              <div className="font-medium text-label">{editMaterial?.text || "—"}</div>
              {editMaterial?.bemerkung ? (
                <div className="mt-1 text-xs text-label-secondary">{editMaterial.bemerkung}</div>
              ) : null}
            </div>
            <TextInput
              label="Menge"
              type="number"
              min={0}
              step="0.01"
              value={form.menge}
              onChange={(e) => patch({ menge: e.target.value })}
            />
          </>
        ) : (
          <>
            <LockedAutocomplete<HeroArticleSuggestion>
              ref={materialAcRef}
              label="Material"
              placeholder="Material suchen oder frei eingeben …"
              value={materialOption}
              minChars={2}
              allowFreeText
              allowEditLocked={(opt) => !isHeroMaterialOption(opt)}
              createFreeTextOption={manualMaterialToOption}
              loadOptions={timesheetMaterialSource}
              onChange={handleMaterialChange}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <TextInput
                label="Menge"
                type="number"
                min={0}
                step="0.01"
                value={form.menge}
                onChange={(e) => patch({ menge: e.target.value })}
              />
              <div className="[&_.eh-input]:text-sm">
                <label className="mb-1 block text-xs font-medium text-label-secondary">Einheit</label>
                <LockedAutocomplete<string>
                  placeholder="Einheit"
                  value={unitValue}
                  minChars={0}
                  loadOptions={async (q) =>
                    filterMaterialUnits(q).map((u) => ({ key: u, label: u, value: u }))
                  }
                  onChange={(opt) => patch({ einheit: opt?.value ?? "" })}
                />
              </div>
              <TextInput
                label="EAN"
                value={form.ean}
                placeholder="EAN"
                inputMode="numeric"
                autoComplete="off"
                onChange={(e) => patch({ ean: e.target.value })}
              />
              <TextInput
                label="Artikelnummer"
                value={form.hero_nr}
                readOnly
                onChange={() => {}}
              />
            </div>
          </>
        )}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Dialog>
  );
}

function MaterialDialogFooter({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: () => void;
}) {
  const requestClose = useDialogRequestClose(onClose);
  return (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <TimesheetFlowFooterButton type="button" variant="secondary" onClick={requestClose}>
        Abbrechen
      </TimesheetFlowFooterButton>
      <TimesheetFlowFooterButton type="button" onClick={onSubmit}>
        Übernehmen
      </TimesheetFlowFooterButton>
    </div>
  );
}
